import { FunctionComponent } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { LIFETIME_START } from "../utils/lifetime";

export const buildShortcuts = (t: TFunction) => {
	const now = new Date();
	const startOfThisYear = new Date(now.getFullYear(), 0, 1);
	const endOfThisYear = new Date(now.getFullYear(), 11, 31);
	const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
	const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31);

	return {
		lifetime: { text: t("analytics.lifetime"), period: { start: LIFETIME_START, end: now } },
		today: { text: "Today", period: { start: now, end: now } },
		thisWeek: { text: "This Week", period: { start: startOfWeek(now), end: endOfWeek(now) } },
		last7Days: {
			text: "Last 7 Days",
			period: { start: new Date(new Date().setDate(now.getDate() - 7)), end: now },
		},
		last30Days: {
			text: "Last 30 Days",
			period: { start: new Date(new Date().setDate(now.getDate() - 30)), end: now },
		},
		thisMonth: { text: "This Month", period: { start: startOfMonth(now), end: endOfMonth(now) } },
		lastMonth: {
			text: "Last Month",
			period: {
				start: startOfMonth(new Date(new Date().setMonth(now.getMonth() - 1))),
				end: endOfMonth(new Date(new Date().setMonth(now.getMonth() - 1))),
			},
		},
		thisYear: { text: "This Year", period: { start: startOfThisYear, end: endOfThisYear } },
		lastYear: { text: "Last Year", period: { start: startOfLastYear, end: endOfLastYear } },
	};
};

export interface DateFilterPillProps {
	value: DateValueType;
	onChange: (value: DateValueType) => void;
	isStacked?: boolean;
}

/**
 * The date range filter, styled to sit beside the `FilterPill`s.
 *
 * Unlike the other filters this one is **not** wrapped in a `FilterPill`: the library
 * anchors its calendar to its own input and opens on that input's focus, so hiding the
 * input to drive it from an outside trigger leaves a picker that never opens. The input
 * therefore stays the trigger and is styled as the pill, with the calendar icon and the
 * `Period:` prefix overlaid on the left the way the other pills carry theirs.
 *
 * "Lifetime" is the *selected* value when no range is set, not an empty state — so the
 * placeholder is coloured like a value (`placeholder:text-text`). Left at the browser's
 * default grey it reads as "you haven't picked anything yet", which is exactly the
 * complaint in PREC-3835.
 */
export const DateFilterPill: FunctionComponent<DateFilterPillProps> = ({
	value,
	onChange,
	isStacked = false,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={isStacked ? "relative w-full" : "relative w-fit"}
			data-testid="analytics-filter-date"
		>
			{/*
			 * Overlaid rather than rendered beside the input: the input *is* the pill, so a
			 * sibling label would sit outside its border and break the pill shape.
			 */}
			<div className="pointer-events-none absolute left-2 top-1/2 z-10 flex -translate-y-1/2 flex-row items-center gap-1.5">
				<Icon className="w-3 h-3 text-text-disabled" icon={faCalendar} />
				<span className="text-xs font-medium text-text-disabled">
					{t("analytics.filters.period")}:
				</span>
			</div>

			<Datepicker
				configs={{ shortcuts: buildShortcuts(t) }}
				containerClassName="relative"
				displayFormat="MMM DD, YYYY"
				inputClassName={`${isStacked ? "w-full" : "w-[256px]"} cursor-pointer rounded-lg border border-border-light bg-brand-white py-[5px] pl-[78px] pr-6 text-xs font-medium text-text duration-200 placeholder:text-text hover:bg-background-light focus:outline-none`}
				maxDate={new Date()}
				placeholder={t("analytics.lifetime")}
				popoverDirection="down"
				primaryColor="blue"
				separator="-"
				showFooter={true}
				showShortcuts={true}
				toggleClassName="absolute right-1 top-1/2 -translate-y-1/2 text-text-disabled"
				useRange={true}
				value={value}
				onChange={onChange}
			/>
		</div>
	);
};
